import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { PartnerOnboardingIDVerification, withStyles } from 'vopay';
import { BodyText, Button, Card, CircleButton } from 'UI';
import useIDVerificationRoleLabels from '@/components/OnboardingTracker/useIDVerificationRoleLabels';
import styles from './IDVerificationCard.module.scss';

const cn = withStyles(styles);

type IDVerificationCardProps = {
  /** The application's `IDVerifications`; ApplicationProgress renders this card only when there is at least one. */
  idVerifications: PartnerOnboardingIDVerification[];
  /** Viewer: the copy and Resend buttons are disabled. */
  isReadOnly: boolean;
  /**
   * Another drawer action is in flight: Resend is disabled. Copy stays enabled, like the onboarding-link copy,
   * because copying makes no request.
   */
  isActionPending: boolean;
  /** False when the account's owning partner emails its own accounts (Partner Emails Account): no Resend (ID-14303). */
  isOnboardingEmailAllowed: boolean;
  onCopyLinkClick: (link: string) => void;
  onResendClick: (individual: PartnerOnboardingIDVerification) => void;
};

/**
 * Figma `1612:6038` (states `1612:8986` / `1612:9036` / `1612:9087`): the Details tab's ID Verification card, as
 * revised by Melanie's mock-up (Slack, 2026-10-28): each person's status follows their roles, and an unverified
 * person gets the copy and Resend buttons instead of their link.
 */
export default function IDVerificationCard({
  idVerifications,
  isReadOnly,
  isActionPending,
  isOnboardingEmailAllowed,
  onCopyLinkClick,
  onResendClick,
}: IDVerificationCardProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'OnboardingTracker.ApplicationProgress.IDVerificationCard',
  });

  const { resolveIDVerificationRoleLabel } = useIDVerificationRoleLabels();
  const personIDPrefix = useId();

  return (
    <Card variant="section" className={cn('card')} data-testid="idVerificationCard">
      <div className={cn('header')}>
        <BodyText weight="md" className={cn('secondaryText')}>
          {t('title', { defaultValue: 'ID Verification' })}
        </BodyText>

        <BodyText size="sm" className={cn('secondaryText')}>
          {t('numVerified', {
            defaultValue: '{{count}} of {{numTotal}} verified',
            count: idVerifications.filter(({ IsVerified }) => IsVerified).length,
            numTotal: idVerifications.length,
          })}
        </BodyText>
      </div>

      <ul className={cn('list')}>
        {idVerifications.map((individual) => (
          <li key={individual.IndividualID} className={cn('row')}>
            <div className={cn('person')} id={`${personIDPrefix}-${individual.IndividualID}`}>
              <BodyText weight="md">{individual.FullName}</BodyText>

              <BodyText size="sm" className={cn('secondaryText')}>
                {/* The mock-up always shows a role; the spec's `x-empty-when` allows none (no person form names
                    the individual's email). Judgement call: the status stands alone then. */}
                {individual.Roles.length > 0 && `${individual.Roles.map(resolveIDVerificationRoleLabel).join(', ')} · `}

                <BodyText as="span" size="sm" className={cn(individual.IsVerified ? 'verified' : 'pending')}>
                  {individual.IsVerified
                    ? t('verified', { defaultValue: 'Verified' })
                    : t('pending', { defaultValue: 'Pending' })}
                </BodyText>
              </BodyText>
            </div>

            <IDVerificationActions
              individual={individual}
              aria-describedby={`${personIDPrefix}-${individual.IndividualID}`}
              isReadOnly={isReadOnly}
              isActionPending={isActionPending}
              isOnboardingEmailAllowed={isOnboardingEmailAllowed}
              onCopyLinkClick={onCopyLinkClick}
              onResendClick={onResendClick}
            />
          </li>
        ))}
      </ul>
    </Card>
  );
}

type IDVerificationActionsProps = {
  individual: PartnerOnboardingIDVerification;
  /** The element holding the person's name, roles and status, which describes the copy and Resend buttons. */
  'aria-describedby': string;
  isReadOnly: boolean;
  isActionPending: boolean;
  isOnboardingEmailAllowed: boolean;
  onCopyLinkClick: (link: string) => void;
  onResendClick: (individual: PartnerOnboardingIDVerification) => void;
};

/** A row's right side: an unverified person's copy and Resend buttons. */
function IDVerificationActions({
  individual,
  'aria-describedby': ariaDescribedBy,
  isReadOnly,
  isActionPending,
  isOnboardingEmailAllowed,
  onCopyLinkClick,
  onResendClick,
}: IDVerificationActionsProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'OnboardingTracker.ApplicationProgress.IDVerificationCard',
  });

  // Not drawn: an unverified person with no stored link has nothing to copy, so the row ends at their status
  // (plan, "Cut for time" 3). Resend goes with the link: spec §6.10 offers it only once Onfido has been
  // triggered, and never for a verified person.
  const link = individual.Link;
  if (individual.IsVerified || !link) return null;

  return (
    <div className={cn('actions')}>
      <CircleButton
        icon="content_copy"
        size="large"
        colorScheme="action"
        circleFill={false}
        // CircleButton logs its aria-label to Datadog on every click, so the label stays free of the person's
        // name; the name reaches assistive tech as the button's description instead.
        aria-label={t('copyLink', { defaultValue: 'Copy the ID verification link' })}
        aria-describedby={ariaDescribedBy}
        disabled={isReadOnly}
        onClick={() => onCopyLinkClick(link)}
      />

      {/* No link-status gate, unlike the collaborator Resend: the backend resends on a submitted or expired
          application too, like the Admin Portal. The static label keeps the person's name out of the Datadog
          click log; the description names them for assistive tech, as on the copy button. Copy stays when
          VoPay doesn't send the account's emails: the partner can still hand the link over itself. */}
      {isOnboardingEmailAllowed && (
        <Button
          size="small"
          aria-describedby={ariaDescribedBy}
          disabled={isReadOnly || isActionPending}
          onClick={() => onResendClick(individual)}
        >
          {t('resend', { defaultValue: 'Resend' })}
        </Button>
      )}
    </div>
  );
}
